import OrderIndex from '@anm/components/OrderIndex';
import Image from '@anm/components/image/Image';
import getActiveIconSrc from '@anm/helpers/get/getActiveIconSrc';
import isMobile from '@anm/helpers/is/isMobile';
import isMobileOrIpad from '@anm/helpers/is/isMobileOrIPad';
import noop from '@anm/helpers/noop';
import useHover from '@anm/hooks/useHover';
import { useCallback, FC, MouseEvent, RefObject } from 'react';

import { DragIcon, HoverIcon, ListItemWrapper, TextWrapper, TitleWrapper, VideoPreview } from './Wrapper';
import { ListItem, SelectionItemProps } from './types';

export { ListItemWrapper, DragIcon };

const SelectionItem: FC<SelectionItemProps> = ({
  index,
  order,
  onChange,
  onRemove,
  onLoadError,
  canDrag = false,
  showTitle = false,
  hasWrapper = false,
  isSelected,
  renderMenu,
  renderContent,
  onDragStart,
  isBackground,
  videoPrevieSrc,
  isSelectionDisabled,
  iconSrc: initialSrc,
  hoverIconVariant = 'right-corner',
  selectedItemVariant = 'thin-border',
  containActiveIcons = false,
  ...item
}) => {
  const iconSrc = getActiveIconSrc({
    initialSrc,
    isSelected,
    containActiveIcons
  });

  const handleRemove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!item.id) return;

      e.stopPropagation();
      onRemove?.(item.id);
    },
    [item.id]
  );

  const isMobileDevice = isMobile();

  const [hoverRef, isHovered] = useHover<HTMLLIElement | HTMLDivElement>(isMobileDevice ? 100 : 0);
  const canShowRemoveIcon = (isMobileDevice ? true : isHovered) && !!onRemove && item.id && !renderMenu;
  const canRenderMenu = (isMobileDevice ? true : isHovered) && !!renderMenu;

  const handleLoadError = useCallback(() => item.id && onLoadError?.(item.id), [item.id, onLoadError]);

  const handleClick = useCallback(
    () =>
      onChange({
        iconSrc,
        ...item
      } as ListItem),
    [onChange]
  );

  if (!renderContent && !iconSrc && !item.text) return null;

  return (
    <ListItemWrapper
      {...(hasWrapper
        ? {
            as: 'div',
            ref: hoverRef as RefObject<HTMLDivElement>
          }
        : {
            as: 'li',
            ref: hoverRef as RefObject<HTMLLIElement>
          })}
      {...{ order, isSelected, selectedItemVariant }}
      isPaid={item.isPaid}
      title={item.name}
      isBackground={isBackground}
      onClick={isSelectionDisabled ? noop : handleClick}
    >
      {typeof index === 'number' && <OrderIndex {...{ index }} />}
      {canShowRemoveIcon && <HoverIcon variant={hoverIconVariant} onClick={handleRemove} title="Remove logo" />}
      {item.text && <TextWrapper>{item.text}</TextWrapper>}
      {renderContent?.() ||
        (iconSrc && <Image draggable={false} src={iconSrc} alt={item.name} onError={handleLoadError} />)}
      {canRenderMenu && renderMenu?.()}
      {isHovered && videoPrevieSrc && <VideoPreview src={videoPrevieSrc} />}
      {showTitle && item.name && (
        <TitleWrapper>
          <span>{item.name}</span>
        </TitleWrapper>
      )}
      {canDrag && isHovered && !isMobileOrIpad() && (
        <DragIcon draggable={true} onDragStart={() => onDragStart?.(item?.id)} onClick={e => e.stopPropagation()} />
      )}
    </ListItemWrapper>
  );
};

export default SelectionItem;
